/**
 * **초기 설정의 판단 한 벌** — 걸음의 상태 기계 · 끝낼 때 무엇을 쓰나 · 체크리스트.
 *
 * ```
 *   걸음       우리 브랜드 → 자사 계정 → 지켜볼 대상 → 알림 → 끝(finish)  — 걸음마다 건너뛸 수 있다
 *   끝내기     고른 것을 차례대로 건다(브랜드 → 계정 → 지켜볼 대상 세트 → 알림) → done · 하나도 없으면 dismissed
 *   체크리스트  저장된 값에서 센다 — 브랜드 키워드 · 세트 · 알림 · Slack (상태와 시각만 저장한다)
 * ```
 *
 * 🚨 **끝낼 때 한꺼번에 쓴다** — 걸음 사이에는 아무것도 안 쓴다. 지켜볼 대상 세트의 알림이 마지막 걸음에서
 *   정해지기 때문이고, 미리보기(`?preview=1`)가 쓰지 않고도 같은 길을 밟을 수 있어서다.
 * ⚠️ `'use client'` 를 붙이지 않는다 — 순수 함수다.
 */

import type { OnboardingStep } from 'src/types/analytics/events';
import type { WorkspaceOnboarding } from 'src/auth/context/auth-context-value';
import type { TrackRegisterInitial } from 'src/utils/workspace/track-register';
import type { DictionaryEntityType } from 'src/types/dictionary/dictionary-ops';

import { paths } from 'src/routes/paths';

import { fNumber } from 'src/utils/format/format-number';
import { normalizeReturnToParam } from 'src/utils/auth/return-to-storage';
import { resolvePostLoginPath } from 'src/utils/auth/resolve-post-login-path';
import { visibleQuickAlertState } from 'src/utils/alert-rule/alert-quick-pick';
import { parseChannelProfileUrl } from 'src/utils/platform/channel-profile-url';

import { DICTIONARY_ENTITY_LABEL } from 'src/constants/dictionary-entity';

export type { OnboardingStep } from 'src/types/analytics/events';

/** 걸음 차례 — 진행 표시 「1/4」 의 순서다 */
export const ONBOARDING_STEPS: readonly OnboardingStep[] = ['brand', 'account', 'competitors', 'alerts'];

/**
 * 브랜드만 담은 세트의 기본 이름 — 홈의 「경쟁」 칸·한 번에 찾기가 이 이름의 세트를 경쟁 세트로 읽는다.
 * ⚠️ 이름을 바꾸지 마라 — `server/find-home/find-home-interests` · `sections/find` 가 같은 말로 찾는다.
 */
export const COMPETITOR_SET_NAME = '경쟁사';

/** 브랜드 밖의 것(성분·제품군·키워드·계정)이 섞인 세트의 기본 이름 */
export const WATCH_SET_NAME = '관심 키워드';

/**
 * 지켜볼 대상을 한 번에 담는 수 — 타겟 멘션 추적을 처음 주는 플랜(BRAND)의 칸 수다.
 * 플랜에 남은 칸이 더 적으면 그만큼이고, 더 걸 것은 추적 관리에서 더한다.
 */
export const WATCH_TARGET_MAX = 10;

/** 초기 설정 진행 상태 */
export type OnboardingStatus = NonNullable<WorkspaceOnboarding['status']>;

/**
 * 첫 장 「무엇부터 해 볼까요?」를 세울까 — 초기 설정에 들어올 때마다(상태 · `?watch=` · `?ownBrand=` 와 무관).
 * 서지 않는 것은 관리자 미리보기가 걸음을 집어 열었을 때(`?preview=1&step=`)뿐이다 · 「브랜드 추적 설정하기」를 고르면 이 화면에서만 걷힌다.
 * 🔑 `?watch=` · `?ownBrand=` 로 채운 칸은 첫 장을 거쳐도 남는다(모델 상태가 첫 장 동안에도 살아 있다).
 * @param previewStep 미리보기가 집은 걸음 — 미리보기가 아니면 `null`
 * @param chosen 이 화면에서 「브랜드 추적 설정하기」를 골랐다(다시 열면 거짓)
 */
export function shouldShowStartChoice(input: { previewStep: OnboardingStep | null; chosen: boolean }): boolean {
  return !input.previewStep && !input.chosen;
}

/** 검색 결과 머리 「우리 브랜드로 추적할까요?」 줄 — 누르면 어디로 가나 */
export type OwnBrandNudge = { name: string; action: { kind: 'start'; href: string } | { kind: 'register'; initial: TrackRegisterInitial } };

/**
 * 검색 결과 머리에 「‘브랜드’을/를 우리 브랜드로 추적할까요?」 줄을 세울까 — 찾은 것이 사전 **브랜드**이고 그 브랜드를 우리 브랜드 칸에 걸 수 있을 때.
 *
 * 서는 조건(모두): 고른 항목이 브랜드(`brand:…` — 키워드 · 성분 · 고민 · @계정은 아님) · 지금 연 워크스페이스가 있다(멤버 · 관리자 모두) · 브랜드 멘션 추적이 플랜 안 ·
 * 브랜드 키워드 칸이 남음(한도를 모르면 막지 않는다) · 그 브랜드가 이미 브랜드 키워드에 없음(대소문자 무시 — 찾은 말 · 정규명 둘 다 본다).
 * 누르면 — 초기 설정을 아직 안 끝낸(`pending`) 워크스페이스는 초기 설정 ① 에 그 브랜드를 채워 열고(`startOwnBrand`), 그 밖은 추적 등록 창을 브랜드 갈래 · 그 이름으로 연다.
 * 🔑 모든 추적 플랜이 주는 칸은 우리 브랜드 칸이다 — 찾은 브랜드를 우리 브랜드로 걸자고 한다.
 * @param input.pick 지금 결과의 고른 항목(`brand:아로셀` 을 읽은 것) — 없으면 서지 않는다
 * @param input.word 찾은 말(화면에 적는 이름 · 채워 넣는 이름)
 * @param input.brandLimit 브랜드 키워드 한도(`toTrackLimit`) — 모르면 `null`
 * @returns 서지 않으면 `null`
 */
export function toOwnBrandNudge(input: {
  pick: { type: string; id: string } | null;
  word: string;
  hasWorkspace: boolean;
  status: unknown;
  brandKeywords: readonly string[];
  brandLimit: number | null;
  brandLocked: boolean;
}): OwnBrandNudge | null {
  if (input.pick?.type !== 'brand' || !input.hasWorkspace || input.brandLocked) return null;
  const name = input.word.trim() || input.pick.id.trim();
  if (!name) return null;
  const room = toSlotRoom(input.brandLimit, input.brandKeywords.length);
  if (room !== null && room <= 0) return null;
  const names = new Set([name, input.pick.id].map((one) => one.trim().toLowerCase()).filter(Boolean));
  if (input.brandKeywords.some((keyword) => names.has(keyword.trim().toLowerCase()))) return null;
  return {
    name,
    action: input.status === 'pending'
      ? { kind: 'start', href: paths.service.startOwnBrand(name) }
      : { kind: 'register', initial: { type: 'brand', value: name } },
  };
}

// ---- 걸음의 상태 기계 ----------------------------------------------------

/** 우리 브랜드 — `known` 은 사전에 있는 이름인가, `suggested` 는 추천에서 골랐나 */
export type OnboardingBrand = { name: string; known: boolean; suggested: boolean };
/** 자사 계정 한 짝 — `suggested` 는 공식 계정 추천에서 골랐나 */
export type OnboardingAccount = { platform: string; username: string; suggested: boolean };
/** 지켜볼 대상의 종류 — 사전 갈래 넷 · 사전에 없는 말(키워드) · 계정 */
export type OnboardingTargetKind = DictionaryEntityType | 'keyword' | 'account';
/**
 * 지켜볼 대상 하나 — 세트의 키워드 한 칸 또는 계정 한 칸이다.
 * `value` 는 키워드면 그 말, 계정이면 `@` 뗀 아이디(`platform` 이 채널) · `suggested` 는 추천 목록에서 골랐나.
 * ⚠️ 종류는 화면 표시용이다 — 세트에는 키워드·계정 두 칸으로만 저장된다.
 */
export type OnboardingTarget = { kind: OnboardingTargetKind; value: string; platform?: string; suggested: boolean };
/** 알림 — 이메일 둘(Slack 은 초기 설정에서 켜지 않는다) */
export type OnboardingAlerts = { daily: boolean; weekly: boolean };

export type OnboardingDraft = {
  step: OnboardingStep | 'finish';
  brand: OnboardingBrand | null;
  accounts: OnboardingAccount[];
  targets: OnboardingTarget[];
  /** 세트 이름 — 비면 담은 것에 맞는 기본 이름(`defaultWatchSetName`) */
  setName: string;
  alerts: OnboardingAlerts;
  /** 건너뛴 걸음 — 끝낼 때 그 걸음의 것은 쓰지 않는다 */
  skipped: OnboardingStep[];
};

export type OnboardingAction =
  | { type: 'confirmBrand'; brand: OnboardingBrand }
  | { type: 'clearBrand' }
  | { type: 'addAccount'; account: OnboardingAccount }
  | { type: 'removeAccount'; platform: string; username: string }
  /** 담기/빼기 — `addOnly` 면 이미 있을 때 빼지 않는다(칸에 친 말·추천 줄은 담기만 한다) */
  | { type: 'toggleTarget'; target: OnboardingTarget; max: number; addOnly?: boolean }
  | { type: 'setSetName'; name: string }
  | { type: 'setAlerts'; alerts: OnboardingAlerts }
  | { type: 'next' }
  | { type: 'skip' }
  | { type: 'back' }
  | { type: 'jump'; step: OnboardingStep };

/** 알림의 처음 값 — 매일 아침 이메일 켬 · 매주 요약 끔 */
export const DEFAULT_ONBOARDING_ALERTS: OnboardingAlerts = { daily: true, weekly: false };

/** 처음 상태 — `step` 은 미리보기가 걸음을 골라 열 때만 준다 */
export function initialOnboardingDraft(step: OnboardingStep = 'brand', brand: OnboardingBrand | null = null): OnboardingDraft {
  return { step, brand, accounts: [], targets: [], setName: '', alerts: DEFAULT_ONBOARDING_ALERTS, skipped: [] };
}

/**
 * 걸음 한 번.
 *
 * 🔑 「다음」은 그 걸음을 마친 것이다(건너뛴 표가 있었으면 지운다) · 「건너뛰기」는 표를 남기고 넘어간다.
 * ⚠️ 우리 브랜드를 바꾸면 **추천에서 고른** 대상을 뺀다 — 후보가 그 브랜드에서 나왔다. 직접 적은 것은 둔다.
 * ⚠️ 지켜볼 대상은 `max` 를 넘겨 담지 못한다 · 우리 브랜드와 같은 말은 담지 못한다.
 */
export function onboardingReducer(state: OnboardingDraft, action: OnboardingAction): OnboardingDraft {
  switch (action.type) {
    case 'confirmBrand': {
      const name = action.brand.name.trim();
      if (!name) return state;
      const same = state.brand && sameName(state.brand.name, name);
      return { ...state, brand: { ...action.brand, name }, targets: same ? state.targets : manualOnly(state.targets) };
    }
    case 'clearBrand':
      return { ...state, brand: null, targets: manualOnly(state.targets) };
    case 'addAccount': {
      const username = action.account.username.trim().replace(/^@+/, '');
      const platform = action.account.platform.trim();
      if (!username || !platform) return state;
      if (state.accounts.some((a) => a.platform === platform && sameName(a.username, username))) return state;
      return { ...state, accounts: [...state.accounts, { ...action.account, platform, username }] };
    }
    case 'removeAccount':
      return {
        ...state,
        accounts: state.accounts.filter((a) => !(a.platform === action.platform && sameName(a.username, action.username))),
      };
    case 'toggleTarget': {
      const target = cleanTarget(action.target);
      if (!target) return state;
      if (target.kind !== 'account' && state.brand && sameName(state.brand.name, target.value)) return state;
      const key = targetKey(target);
      if (state.targets.some((t) => targetKey(t) === key)) {
        if (action.addOnly) return state;
        return { ...state, targets: state.targets.filter((t) => targetKey(t) !== key) };
      }
      if (state.targets.length >= Math.max(0, action.max)) return state;
      return { ...state, targets: [...state.targets, target] };
    }
    case 'setSetName':
      return { ...state, setName: action.name };
    case 'setAlerts':
      return { ...state, alerts: action.alerts };
    case 'next':
      if (state.step === 'finish') return state;
      return { ...state, skipped: state.skipped.filter((s) => s !== state.step), step: stepAfter(state.step) };
    case 'skip':
      if (state.step === 'finish') return state;
      return {
        ...state,
        skipped: state.skipped.includes(state.step) ? state.skipped : [...state.skipped, state.step],
        step: stepAfter(state.step),
      };
    case 'back': {
      if (state.step === 'finish') return { ...state, step: ONBOARDING_STEPS[ONBOARDING_STEPS.length - 1] };
      const index = ONBOARDING_STEPS.indexOf(state.step);
      return index > 0 ? { ...state, step: ONBOARDING_STEPS[index - 1] } : state;
    }
    case 'jump':
      return { ...state, step: action.step };
    default:
      return state;
  }
}

/**
 * 「다음」을 누를 수 있나 — 우리 브랜드는 확인해야, 지켜볼 대상은 하나 이상 담아야 한다.
 * 🔑 자사 계정은 **선택 걸음**이라 늘 열린다 — 하나도 안 담고 누르면 건너뛰기와 같다(`isOptionalEmpty`).
 */
export function canCompleteStep(state: OnboardingDraft): boolean {
  if (state.step === 'brand') return Boolean(state.brand);
  if (state.step === 'competitors') return state.targets.length > 0;
  return true;
}

/** 선택 걸음을 비운 채로 「다음」 — 마친 것이 아니라 건너뛴 것으로 센다 */
export function isOptionalEmpty(state: OnboardingDraft): boolean {
  return state.step === 'account' && state.accounts.length === 0;
}

/**
 * 담아 둔 상태(`sessionStorage`)를 다시 읽는다 — 새로고침·화면 다시 그리기에도 고른 것이 남게.
 * ⚠️ 모양이 어긋나면 `null` 이다(처음부터) — 끝(`finish`)은 담지 않는다.
 * ⚠️ `competitors`(이름만 담던 꼴)도 읽는다 — 열려 있던 탭의 세션 저장본이 브랜드 대상으로 돌아온다.
 */
export function readStoredDraft(raw: unknown): OnboardingDraft | null {
  if (!raw || typeof raw !== 'object') return null;
  const d = raw as Record<string, any>;
  if (!ONBOARDING_STEPS.includes(d.step)) return null;
  const brand = d.brand && typeof d.brand.name === 'string' && d.brand.name.trim()
    ? { name: d.brand.name.trim(), known: Boolean(d.brand.known), suggested: Boolean(d.brand.suggested) }
    : null;
  const list = (value: unknown) => (Array.isArray(value) ? value : []);
  return {
    step: d.step,
    brand,
    accounts: list(d.accounts)
      .filter((a) => typeof a?.platform === 'string' && typeof a?.username === 'string' && a.username)
      .map((a) => ({ platform: a.platform, username: a.username, suggested: Boolean(a.suggested) })),
    targets: [
      ...list(d.targets).map((t) => cleanTarget({
        kind: TARGET_KINDS.includes(t?.kind) ? t.kind : 'keyword',
        value: String(t?.value ?? ''),
        platform: typeof t?.platform === 'string' ? t.platform : undefined,
        suggested: Boolean(t?.suggested),
      })),
      ...list(d.competitors).map((c) => cleanTarget({ kind: 'brand', value: String(c?.name ?? ''), suggested: Boolean(c?.suggested) })),
    ].filter((t): t is OnboardingTarget => t !== null),
    setName: typeof d.setName === 'string' ? d.setName : '',
    alerts: { daily: d.alerts?.daily !== false, weekly: d.alerts?.weekly === true },
    skipped: list(d.skipped).filter((s): s is OnboardingStep => ONBOARDING_STEPS.includes(s)),
  };
}

/** 진행 표시 — 「1/4」(끝이면 「4/4」) */
export function onboardingProgress(step: OnboardingDraft['step']): string {
  const index = step === 'finish' ? ONBOARDING_STEPS.length : ONBOARDING_STEPS.indexOf(step) + 1;
  return `${index}/${ONBOARDING_STEPS.length}`;
}

// ---- 지켜볼 대상 -----------------------------------------------------------

/**
 * 직접 친 말 하나 → 지켜볼 대상.
 *
 * ```
 *   https://instagram.com/anua_kr     계정(주소의 채널)
 *   @anua_kr                          계정(고른 채널 `platform`)
 *   메디힐 · 나이아신아마이드 · 토너   사전 추천에 똑같은 이름이 있으면 그 갈래, 없으면 키워드
 * ```
 * @param platform `@아이디` 일 때 붙일 채널 — 주소면 주소의 채널이 이긴다
 * @param suggestions 지금 칸 아래의 사전 추천 — 이름이 똑같을 때만 그 갈래를 붙인다
 * @returns 빈 말 · 읽지 못한 주소면 `null`
 */
export function readTargetInput(
  text: string,
  platform: string,
  suggestions: readonly { type: DictionaryEntityType; id: string; matched?: string }[] = [],
): OnboardingTarget | null {
  const raw = String(text ?? '').trim();
  if (!raw) return null;
  if (looksLikeUrl(raw)) {
    const hit = parseChannelProfileUrl(raw);
    return hit ? { kind: 'account', value: hit.account, platform: hit.channel, suggested: false } : null;
  }
  if (raw.startsWith('@')) {
    const handle = raw.replace(/^@+/, '').trim();
    return handle && platform ? { kind: 'account', value: handle, platform, suggested: false } : null;
  }
  const same = suggestions.find((item) => sameName(item.id, raw) || (item.matched ? sameName(item.matched, raw) : false));
  return same ? { kind: same.type, value: same.id, suggested: false } : { kind: 'keyword', value: raw, suggested: false };
}

/** 칸에 친 말이 계정인가 — `@` 로 시작하거나 주소다(이때는 사전 추천을 묻지 않는다) */
export function isAccountInput(text: string): boolean {
  const raw = String(text ?? '').trim();
  return raw.startsWith('@') || looksLikeUrl(raw);
}

/** 대상의 종류 이름 — 칩·미리보기 줄에 붙는다 */
export const TARGET_KIND_LABEL: Record<OnboardingTargetKind, string> = {
  ...DICTIONARY_ENTITY_LABEL,
  keyword: '키워드',
  account: '계정',
};

/** 칩·줄의 이름 — 계정은 「@아이디」 */
export function targetLabel(target: Pick<OnboardingTarget, 'kind' | 'value'>): string {
  return target.kind === 'account' ? `@${target.value}` : target.value;
}

/**
 * 담은 것에 맞는 세트 기본 이름 — 브랜드만이면 「경쟁사」(홈의 경쟁 칸이 읽는 이름), 그 밖이 섞이면 「관심 키워드」.
 */
export function defaultWatchSetName(targets: readonly Pick<OnboardingTarget, 'kind'>[]): string {
  return targets.length > 0 && targets.every((t) => t.kind === 'brand') ? COMPETITOR_SET_NAME : WATCH_SET_NAME;
}

/**
 * 걸 세트 이름 — 사람이 적은 이름 → 초기 설정이 전에 건 세트 이름(다시 들어왔을 때 그 세트에 더한다) → 기본 이름.
 * @param earlier 초기 설정이 전에 건 세트의 이름 — 없으면 빈 글자
 */
export function resolveWatchSetName(state: Pick<OnboardingDraft, 'setName' | 'targets'>, earlier = ''): string {
  return state.setName.trim() || earlier.trim() || defaultWatchSetName(state.targets);
}

/**
 * 담은 것 → 세트의 두 칸 — 키워드(계정 아닌 것 전부)와 계정(`{ platform, account }` · 서버가 읽는 칸 이름).
 * 🚨 세트 문서는 이 두 칸뿐이다 — 종류(브랜드·성분 …)는 저장하지 않는다.
 */
export function toWatchSetFields(targets: readonly OnboardingTarget[]): { keywords: string[]; accounts: { platform: string; account: string }[] } {
  return {
    keywords: targets.filter((t) => t.kind !== 'account').map((t) => t.value),
    accounts: targets.filter((t) => t.kind === 'account' && t.platform).map((t) => ({ platform: t.platform!, account: t.value })),
  };
}

// ---- 끝내기 --------------------------------------------------------------

/** 살아 있는 세트 한 벌 — 이름이 같은 세트가 있으면 새로 만들지 않고 이어 붙인다 */
export type OnboardingSetRef = { id: string; name: string; keywords: readonly string[]; accounts: readonly { platform: string; account: string }[] };

/** 지금 워크스페이스에 걸려 있는 것 — 치환 op 에 넘길 «지금 목록»과 이미 걸린 것 가리기 */
export type OnboardingCurrent = {
  brandKeywords: readonly string[];
  brandAccounts: readonly { platform?: unknown; username?: unknown }[];
  /** 살아 있는 세트들 — 고른 이름과 같은 세트를 찾는다 */
  sets: readonly OnboardingSetRef[];
  /** 초기 설정이 전에 건 세트(`onboarding.setId`) — 다시 들어오면 이름을 안 적어도 그 세트에 더한다 */
  earlierSet?: OnboardingSetRef | null;
};

/** 끝낼 때 거는 것 — 이미 걸린 것과 건너뛴 걸음은 빠진다 */
export type OnboardingCommitPlan = {
  brand: string | null;
  accounts: { platform: string; username: string }[];
  /** 지켜볼 대상 세트 — 새로 걸 키워드·계정과, 이름이 같은 세트가 있으면 그 세트(`existing`) */
  watch: {
    name: string;
    keywords: string[];
    accounts: { platform: string; account: string }[];
    existing: OnboardingSetRef | null;
  };
  /** 알림 걸음을 마쳤으면 그 값 — 건너뛰었으면 `null`(쓰지 않는다) */
  alerts: OnboardingAlerts | null;
  status: Exclude<OnboardingStatus, 'pending'>;
};

/**
 * 끝낼 때 무엇을 쓰나.
 *
 * 🔑 **하나도 걸지 않았으면 `dismissed`** 다(네 걸음을 다 건너뛰었거나, 고른 것이 전부 이미 걸려 있다) —
 *   다시는 저절로 안 띄운다. 무엇이든 걸었으면 `done`.
 * ⚠️ 알림만 고른 것은 «건 것»으로 치지 않는다 — 알 것이 없는 알림이다.
 */
export function toOnboardingCommitPlan(state: OnboardingDraft, current: OnboardingCurrent): OnboardingCommitPlan {
  const skipped = (step: OnboardingStep) => state.skipped.includes(step);
  const brand = !skipped('brand') && state.brand && !current.brandKeywords.some((k) => sameName(k, state.brand!.name))
    ? state.brand.name
    : null;
  const accounts = skipped('account')
    ? []
    : state.accounts
      .filter((a) => !current.brandAccounts.some(
        (b) => String(b?.platform ?? '') === a.platform && sameName(String(b?.username ?? ''), a.username),
      ))
      .map(({ platform, username }) => ({ platform, username }));
  const name = resolveWatchSetName(state, current.earlierSet?.name ?? '');
  const existing = current.sets.find((set) => set.name.trim() === name) ?? null;
  const fields = toWatchSetFields(skipped('competitors') ? [] : state.targets);
  const keywords = fields.keywords.filter((k) => !(existing?.keywords ?? []).some((e) => sameName(e, k)));
  const watchAccounts = fields.accounts.filter((a) => !(existing?.accounts ?? []).some(
    (e) => e.platform === a.platform && sameName(e.account, a.account),
  ));
  const tracksSomething = Boolean(brand) || accounts.length > 0 || keywords.length > 0 || watchAccounts.length > 0;
  return {
    brand,
    accounts,
    watch: { name, keywords, accounts: watchAccounts, existing },
    alerts: skipped('alerts') ? null : state.alerts,
    status: tracksSomething ? 'done' : 'dismissed',
  };
}

/** 새로 걸 지켜볼 대상 수 — 키워드 + 계정 */
export function watchCount(plan: Pick<OnboardingCommitPlan, 'watch'>): number {
  return plan.watch.keywords.length + plan.watch.accounts.length;
}

/**
 * 상태 표를 쓰는 한 번(`workspace.fields.update`) — 점 경로 + 서버 시각 도장.
 *
 * 🔑 **다시 들어와 끝낸 것은 처음 끝낸 기록을 덮지 않는다** — 이미 `done` 이면 시각을 다시 찍지 않고,
 *   세트가 새로 생겼을 때만 `onboarding.setId` 를 갈아 적는다. 쓸 것이 없으면 `null`.
 * ⚠️ `dismissed` 는 아직 `pending` 일 때만 적는다 — 끝낸 워크스페이스를 «접음»으로 덮지 않는다.
 * ⚠️ 시각은 payload 에 싣지 않는다 — `serverTimeFields` 이름으로 넘겨야 서버가 도장을 찍는다.
 * @param setId 초기 설정이 건 세트 — 있으면 `onboarding.setId` 로 적는다(다시 들어오면 그 세트에 이어 붙인다)
 * @param prev 지금 저장된 상태와 세트 id — 모르면 처음 끝내는 것으로 본다
 */
export function toOnboardingStatusWrite(
  status: Exclude<OnboardingStatus, 'pending'>,
  setId = '',
  prev: { status?: unknown; setId?: unknown } = { status: 'pending' },
): { payload: Record<string, string>; serverTimeFields: string[] } | null {
  if (status === 'dismissed' && prev.status !== 'pending') return null;
  if (prev.status === 'done') {
    if (status !== 'done' || !setId || setId === String(prev.setId ?? '')) return null;
    return { payload: { 'onboarding.setId': setId }, serverTimeFields: [] };
  }
  return {
    payload: { 'onboarding.status': status, ...(setId ? { 'onboarding.setId': setId } : {}) },
    serverTimeFields: [status === 'done' ? 'onboarding.completedAt' : 'onboarding.dismissedAt'],
  };
}

/**
 * 끝낸 뒤 · 「나중에 할게요」 뒤 가는 곳 — 초기 설정 주소에 `returnTo`(들어오기 전 보던 화면)가 있으면 그 화면, 없으면 **서비스 첫 화면(홈)**.
 * 🔑 홈이 걸어 둔 추적 · 진행 중인 일을 한 장에 모아 보여 주므로, 걸은 것에 따라 한 추적 화면으로 보내지 않는다.
 * 🔑 바깥 주소 · 초기 설정 자신은 홈으로 접는다.
 * @param search 초기 설정 주소의 질의(`?returnTo=…`) — 없으면 홈
 */
export function toOnboardingDestination(search = ''): string {
  const back = normalizeReturnToParam(new URLSearchParams(search).get('returnTo'));
  if (!back || back.split('?')[0].replace(/\/+$/, '') === paths.service.start) return paths.service.root;
  return resolvePostLoginPath(back);
}

// ---- 칸 줄 ---------------------------------------------------------------

/**
 * 플랜 칸 형편 — 걸음마다 「몇 개까지」를 적는 재료.
 * @param limit 플랜 한도 — 모르면 `null`
 * @param used 이미 쓴 칸
 */
export function toSlotRoom(limit: number | null, used: number): number | null {
  if (limit === null) return null;
  return Math.max(0, limit - used);
}

/**
 * 우리 브랜드 칸 줄 — 「브랜드 키워드 1/2 사용 · 체험 중」.
 * @param used 이미 건 것 + 이번에 고른 것
 * @param trial 체험 플랜인가 — 맞으면 「· 체험 중」을 붙인다
 */
export function toBrandSlotLine(used: number, limit: number | null, trial: boolean): string {
  const usage = limit === null ? `${fNumber(used)}개 사용` : `${fNumber(used)}/${fNumber(limit)} 사용`;
  return `브랜드 키워드 ${usage}${trial ? ' · 체험 중' : ''}`;
}

/**
 * 지켜볼 대상은 몇 개까지 담나 — `WATCH_TARGET_MAX` 와 남은 타겟 키워드 칸(키워드 + 계정) 중 작은 것.
 * @param room 남은 타겟 키워드 칸 — 모르면 `null`
 */
export function watchTargetMax(room: number | null): number {
  return room === null ? WATCH_TARGET_MAX : Math.min(WATCH_TARGET_MAX, room);
}

/**
 * 지켜볼 대상 칸 줄 — 사람에게 뜻 있는 수만 적는다.
 *
 * ```
 *   체험                       담은 것 2/3 · 체험은 3개까지
 *   플랜 칸이 한 번 담는 수보다 적다  담은 것 2/6 · 지금 플랜에서 더 걸 수 있는 만큼
 *   그 밖(칸이 넉넉하다)         담은 것 2/10 · 더 걸 것은 추적 관리에서 더할 수 있어요
 * ```
 * ⚠️ 남은 칸 수 자체(「남은 칸 899개」)는 적지 않는다 — 넉넉한 칸의 수는 고르는 데 쓸모가 없다.
 * @param limit 플랜의 타겟 키워드 한도 — 체험이면 그 수를 적는다
 * @param room 남은 타겟 키워드 칸 — 모르면 `null`
 */
export function toWatchSlotLine(picked: number, max: number, trial: boolean, limit: number | null, room: number | null): string {
  const head = `담은 것 ${fNumber(picked)}/${fNumber(max)}`;
  if (trial && limit !== null) return `${head} · 체험은 ${fNumber(limit)}개까지`;
  if (room !== null && room < WATCH_TARGET_MAX) return `${head} · 지금 플랜에서 더 걸 수 있는 만큼`;
  return `${head} · 더 걸 것은 추적 관리에서 더할 수 있어요`;
}

// ---- 함께 언급된 후보 ----------------------------------------------------

/** 후보 한 줄(브랜드·성분) — 이름과 함께 언급된 글 수(대체 목록이면 그 제품군의 언급 수) */
export type CompetitorCandidate = { name: string; count: number };

/**
 * 후보를 다듬는다 — 우리 브랜드·이미 건 브랜드 키워드는 빼고, 같은 이름은 하나로, 앞에서 `size` 개.
 * @param exclude 뺄 이름들(대소문자 무시)
 */
export function toCompetitorCandidates(
  rows: readonly CompetitorCandidate[],
  exclude: readonly string[],
  size: number,
): CompetitorCandidate[] {
  const seen = new Set(exclude.map(norm).filter(Boolean));
  const out: CompetitorCandidate[] = [];
  rows.forEach((row) => {
    const key = norm(row.name);
    if (!key || seen.has(key) || out.length >= size) return;
    seen.add(key);
    out.push({ name: row.name.trim(), count: Number(row.count) || 0 });
  });
  return out;
}

/**
 * 후보를 어디서 받나 — 함께 언급된 브랜드가 있으면 그것, 다 왔는데 비었으면 제품군 대체 목록.
 * @returns `loading` 이면 스켈레톤 줄을 세운다
 */
export function competitorSourceMode(input: { brand: string; coLoading: boolean; coCount: number }): 'co' | 'fallback' | 'loading' {
  if (!input.brand) return 'fallback';
  if (input.coLoading) return 'loading';
  return input.coCount > 0 ? 'co' : 'fallback';
}

// ---- 체크리스트 ----------------------------------------------------------

export type OnboardingChecklistKey = 'brand' | 'competitors';

export type OnboardingChecklistItem = { key: OnboardingChecklistKey; label: string; done: boolean };

export type OnboardingChecklist = { items: OnboardingChecklistItem[]; done: number; total: number };

/**
 * 초기 설정 체크리스트 — **저장된 값에서 센다**(체크 표를 따로 저장하지 않는다).
 *
 * - 브랜드 등록: 브랜드 키워드가 하나 이상
 * - 키워드·계정 추적: 걸 것이 하나라도 든 수집 세트가 있다
 * 🔑 모수는 이 둘뿐이다(사용자 결정) — 알림·Slack 은 세지 않는다(홈 할 일은 `isBrandMentionAlertOn`·`isSlackConnected` 로 따로 묻는다).
 */
export function computeOnboardingChecklist(input: {
  brandKeywords: readonly unknown[];
  campaigns: readonly { keywords?: unknown; accounts?: unknown }[];
}): OnboardingChecklist {
  const hasSet = input.campaigns.some(
    (row) => (Array.isArray(row?.keywords) && row.keywords.length > 0) || (Array.isArray(row?.accounts) && row.accounts.length > 0),
  );
  const items: OnboardingChecklistItem[] = [
    { key: 'brand', label: '브랜드 등록', done: input.brandKeywords.some((k) => String(k ?? '').trim() !== '') },
    { key: 'competitors', label: '키워드·계정 추적', done: hasSet },
  ];
  return { items, done: items.filter((item) => item.done).length, total: items.length };
}

/**
 * 브랜드 멘션 알림 중 하나가 **켬으로 적혀 있나** — 저장값 없음은 «안 정했다»로 읽는다.
 * 「보류」 매월은 `ALERT_MONTHLY_VISIBLE` 이 꺼져 있으면 세지 않는다(`visibleQuickAlertState`).
 * @param monthlyVisible 매월을 세나 — 기본은 스위치 값
 */
export function isBrandMentionAlertOn(
  mention:
    | { messageEmail?: unknown; messageEmailWeekly?: unknown; messageEmailMonthly?: unknown; messageSlackRealTime?: unknown }
    | null
    | undefined,
  monthlyVisible?: boolean,
): boolean {
  const state = visibleQuickAlertState(
    {
      daily: mention?.messageEmail === true,
      weekly: mention?.messageEmailWeekly === true,
      monthly: mention?.messageEmailMonthly === true,
      realtime: mention?.messageSlackRealTime === true,
    },
    monthlyVisible,
  );
  return state.daily || state.weekly || state.monthly || state.realtime;
}

/** 한 줄 — 「초기 설정 1/2 ✓브랜드 등록 · ○키워드·계정 추적」 */
export function toChecklistLine(list: OnboardingChecklist): string {
  const marks = list.items.map((item) => `${item.done ? '✓' : '○'}${item.label}`).join(' · ');
  return `초기 설정 ${list.done}/${list.total} ${marks}`;
}

/**
 * 사이드바 「추적 관리」 옆 배지 — 「1/2」. 다 했거나 대상이 아니면 `null`(배지를 안 단다).
 * @param status 워크스페이스의 `onboarding.status` — 칸이 없으면 대상이 아니다
 */
export function toOnboardingBadge(list: OnboardingChecklist, status: unknown): string | null {
  if (status !== 'pending' && status !== 'done' && status !== 'dismissed') return null;
  if (list.done >= list.total) return null;
  return `${list.done}/${list.total}`;
}

/** Slack 이 연결돼 있나 — `slack.webhookUrl` 또는 `slackUrl`(Slack 연결 콜백이 둘 다 적는다) */
export function isSlackConnected(doc: { slack?: { webhookUrl?: unknown } | null; slackUrl?: unknown } | null | undefined): boolean {
  return Boolean(String(doc?.slack?.webhookUrl ?? '').trim() || String(doc?.slackUrl ?? '').trim());
}

// ---- 비공개 ---------------------------------------------------------------

function stepAfter(step: OnboardingStep): OnboardingDraft['step'] {
  const index = ONBOARDING_STEPS.indexOf(step);
  return index >= 0 && index < ONBOARDING_STEPS.length - 1 ? ONBOARDING_STEPS[index + 1] : 'finish';
}

function norm(value: unknown): string {
  return String(value ?? '').trim().toLowerCase();
}

function sameName(a: string, b: string): boolean {
  return norm(a) === norm(b);
}

const TARGET_KINDS: readonly OnboardingTargetKind[] = ['brand', 'ingredient', 'concern', 'productCategory', 'keyword', 'account'];

/** 다듬은 대상 — 빈 말·채널 없는 계정은 `null` */
function cleanTarget(target: OnboardingTarget): OnboardingTarget | null {
  const value = target.kind === 'account' ? target.value.trim().replace(/^@+/, '') : target.value.trim();
  if (!value) return null;
  if (target.kind !== 'account') return { kind: target.kind, value, suggested: target.suggested };
  const platform = String(target.platform ?? '').trim();
  return platform ? { kind: 'account', value, platform, suggested: target.suggested } : null;
}

/** 같은 대상인가의 열쇠 — 키워드는 종류와 상관없이 말로(세트에는 말로만 저장된다), 계정은 채널 × 아이디 */
function targetKey(target: OnboardingTarget): string {
  return target.kind === 'account' ? `@${target.platform}:${norm(target.value)}` : norm(target.value);
}

function manualOnly(targets: readonly OnboardingTarget[]): OnboardingTarget[] {
  return targets.filter((t) => !t.suggested);
}

function looksLikeUrl(raw: string): boolean {
  return /^https?:\/\//i.test(raw) || /^([a-z0-9-]+\.)+(com|net)\//i.test(raw);
}
